import { useEffect, useMemo, useState } from 'react';
import { Plus, Save, Trash2, Search, Filter, XCircle, ChevronUp, ChevronDown } from 'lucide-react';
import { shareTargetService } from '../services/shareTargetService';
import { WorkiomTablePagination } from '../components/WorkiomTablePagination';
import { exportRowsToExcel } from '../utils/excelExport';

type ShareTargetRow = {
  id?: string;
  name: string;
  platform: 'whatsapp' | 'facebook';
  target_type: 'group' | 'page';
  phone_number?: string;
  target_url?: string;
  is_active?: boolean;
  sort_order?: number;
};

export function ShareTargets() {
  const [rows, setRows] = useState<ShareTargetRow[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<keyof ShareTargetRow | ''>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [filters, setFilters] = useState<Partial<Record<keyof ShareTargetRow, string>>>({});
  const [showFilters, setShowFilters] = useState(false);
  const [showGrouping, setShowGrouping] = useState(false);
  const [groupBy, setGroupBy] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const columns: Array<{ key: keyof ShareTargetRow; label: string }> = [
    { key: 'name', label: 'Ad' },
    { key: 'platform', label: 'Platform' },
    { key: 'target_type', label: 'Tür' },
    { key: 'phone_number', label: 'Telefon' },
    { key: 'target_url', label: 'URL' },
    { key: 'sort_order', label: 'Sıra' },
  ];

  const loadData = async () => {
    const response = await shareTargetService.getTargets(true);
    setRows((response.data || response || []).map((item: any) => ({
      id: String(item.id),
      name: item.name,
      platform: item.platform,
      target_type: item.target_type,
      phone_number: item.phone_number || '',
      target_url: item.target_url || '',
      is_active: Boolean(item.is_active),
      sort_order: Number(item.sort_order || 0),
    })));
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredRows = useMemo(() => rows.filter((row) => {
    const matchesSearch = !searchTerm.trim() || [row.name, row.platform, row.target_type, row.phone_number || '', row.target_url || ''].join(' ').toLocaleLowerCase('tr-TR').includes(searchTerm.trim().toLocaleLowerCase('tr-TR'));
    const matchesFilters = Object.entries(filters).every(([key, value]) => {
      if (!value) return true;
      return String(row[key as keyof ShareTargetRow] ?? '').toLocaleLowerCase('tr-TR').includes(value.toLocaleLowerCase('tr-TR'));
    });
    return matchesSearch && matchesFilters;
  }), [rows, searchTerm, filters]);

  const sortedRows = useMemo(() => {
    if (!sortField) return filteredRows;
    return [...filteredRows].sort((a, b) => {
      const aVal = String(a[sortField] ?? '');
      const bVal = String(b[sortField] ?? '');
      const comparison = aVal.localeCompare(bVal, 'tr');
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [filteredRows, sortField, sortOrder]);

  const paginatedRows = useMemo(() => sortedRows.slice((page - 1) * pageSize, page * pageSize), [sortedRows, page, pageSize]);

  const groupedRows = useMemo(() => {
    if (!groupBy) return { all: paginatedRows };
    return paginatedRows.reduce((acc: Record<string, ShareTargetRow[]>, item) => {
      const key = String(item[groupBy as keyof ShareTargetRow] || '-');
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
  }, [paginatedRows, groupBy]);

  const handleExportExcel = () => exportRowsToExcel(sortedRows as any[], `share-targets-${new Date().toISOString().slice(0, 10)}.xlsx`, { sheetName: 'ShareTargets' });

  const handleSort = (field: keyof ShareTargetRow) => {
    if (sortField === field) {
      setSortOrder((current) => current === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortField(field);
    setSortOrder('asc');
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const saveRow = async (row: ShareTargetRow) => {
    const payload = {
      name: row.name,
      platform: row.platform,
      targetType: row.target_type,
      phoneNumber: row.phone_number,
      targetUrl: row.target_url,
      isActive: Boolean(row.is_active),
      sortOrder: Number(row.sort_order || 0),
    };

    if (row.id) {
      await shareTargetService.updateTarget(row.id, payload);
    } else {
      await shareTargetService.createTarget(payload);
    }
    await loadData();
  };

  const deleteRow = async (row: ShareTargetRow) => {
    if (!row.id) {
      setRows((current) => current.filter((item) => item !== row));
      return;
    }
    await shareTargetService.deleteTarget(row.id);
    await loadData();
  };

  return (
    <div className="workiom-screen">
      <div className="workiom-toolbar">
        <div className="workiom-toolbar-left">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Paylaşım Hedefleri</h1>
            <p className="text-sm text-gray-600 mt-1">WhatsApp grupları ve Facebook hedeflerini yönet</p>
          </div>
        </div>
        <div className="workiom-toolbar-right">
          <button type="button" onClick={handleExportExcel} className="workiom-outline-button">
            Excele Aktar
          </button>
          <button onClick={() => setRows((current) => [...current, { name: '', platform: 'whatsapp', target_type: 'group', phone_number: '', target_url: '', is_active: true, sort_order: current.length + 1 }])} className="workiom-primary-button">
            <Plus className="w-4 h-4" /> Yeni Hedef
          </button>
        </div>
      </div>

      <div className="workiom-toolbar !justify-start !pt-0">
        <div className="flex flex-wrap items-center gap-3 w-full">
          <div className="workiom-search max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Hedef ara..." className="outline-none" />
          </div>
          <button onClick={() => setShowFilters((current) => !current)} className={`workiom-outline-button ${showFilters ? '!bg-blue-50 !border-blue-500 !text-blue-700' : ''}`}><Filter className="w-4 h-4" /> Filtrele</button>
          {activeFilterCount > 0 && <button onClick={clearFilters} className="workiom-outline-button"><XCircle className="w-4 h-4" /> Filtreleri Temizle</button>}
          <div className="flex items-center gap-2">
            <button onClick={() => setShowGrouping((current) => !current)} className={`workiom-outline-button ${showGrouping ? '!bg-blue-50 !border-blue-500 !text-blue-700' : ''}`}><Filter className="w-4 h-4" /> Gruplandır</button>
            {showGrouping && <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg"><option value="">Gruplama Yok</option><option value="platform">Platform</option><option value="target_type">Tür</option></select>}
          </div>
        </div>
      </div>

      <div className="workiom-grid-wrap mx-6 border border-gray-200 bg-white">
        <table className="workiom-grid-table text-sm">
          <thead>
            <tr>
              {columns.map((column) => <th key={column.key} className="text-left cursor-pointer hover:bg-gray-100 select-none" onClick={() => handleSort(column.key)}><div className="flex items-center gap-2">{column.label}{sortField === column.key && (sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}</div></th>)}
              <th className="text-left">Aktif</th>
              <th className="text-left">İşlem</th>
            </tr>
            {showFilters && (
              <tr>
                {columns.map((column) => <th key={`filter-${column.key}`}><input value={filters[column.key] || ''} onChange={(e) => setFilters((current) => ({ ...current, [column.key]: e.target.value }))} placeholder={`${column.label} filtre`} className="w-full rounded border border-gray-300 px-2 py-1 text-xs" /></th>)}
                <th></th>
                <th></th>
              </tr>
            )}
          </thead>
          <tbody>
            {Object.entries(groupedRows).flatMap(([groupKey, groupItems]) => {
              const rendered: JSX.Element[] = [];
              if (groupBy) {
                rendered.push(<tr key={`group-${groupKey}`} className="bg-gray-100"><td colSpan={columns.length + 2} className="px-3 py-2"><button type="button" onClick={() => setCollapsedGroups((current) => ({ ...current, [groupKey]: !current[groupKey] }))} className="flex items-center gap-2 text-sm font-medium text-gray-900">{collapsedGroups[groupKey] ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}<span>{groupKey}</span><span className="text-gray-500">({groupItems.length})</span></button></td></tr>);
              }
              if (!collapsedGroups[groupKey]) {
                rendered.push(...groupItems.map((row, index) => (
              <tr key={row.id || `new-${index}`}>
                <td><input value={row.name} onChange={(e) => setRows((current) => current.map((item) => item === row ? { ...item, name: e.target.value } : item))} className="w-full border rounded-lg px-3 py-2" /></td>
                <td><select value={row.platform} onChange={(e) => setRows((current) => current.map((item) => item === row ? { ...item, platform: e.target.value as any } : item))} className="w-full border rounded-lg px-3 py-2"><option value="whatsapp">WhatsApp</option><option value="facebook">Facebook</option></select></td>
                <td><select value={row.target_type} onChange={(e) => setRows((current) => current.map((item) => item === row ? { ...item, target_type: e.target.value as any } : item))} className="w-full border rounded-lg px-3 py-2"><option value="group">Grup</option><option value="page">Sayfa</option></select></td>
                <td><input value={row.phone_number || ''} onChange={(e) => setRows((current) => current.map((item) => item === row ? { ...item, phone_number: e.target.value } : item))} className="w-full border rounded-lg px-3 py-2" /></td>
                <td><input value={row.target_url || ''} onChange={(e) => setRows((current) => current.map((item) => item === row ? { ...item, target_url: e.target.value } : item))} className="w-full border rounded-lg px-3 py-2" /></td>
                <td><input type="number" value={row.sort_order || 0} onChange={(e) => setRows((current) => current.map((item) => item === row ? { ...item, sort_order: Number(e.target.value) } : item))} className="w-24 border rounded-lg px-3 py-2" /></td>
                <td><input type="checkbox" checked={Boolean(row.is_active)} onChange={(e) => setRows((current) => current.map((item) => item === row ? { ...item, is_active: e.target.checked } : item))} /></td>
                <td>
                  <div className="flex items-center gap-2">
                    <button onClick={() => saveRow(row)} className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-2 text-white text-sm"><Save className="w-4 h-4" /> Kaydet</button>
                    <button onClick={() => deleteRow(row)} className="inline-flex items-center gap-1 rounded-lg bg-rose-600 px-3 py-2 text-white text-sm"><Trash2 className="w-4 h-4" /> Sil</button>
                  </div>
                </td>
              </tr>
                )));
              }
              return rendered;
            })}
          </tbody>
        </table>
      </div>
      <div className="mx-6 mb-6 flex items-center justify-end gap-3">
        <WorkiomTablePagination page={page} pageSize={pageSize} totalRows={filteredRows.length} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} />
      </div>
    </div>
  );
}
